import React from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Mail, Phone, Briefcase } from "lucide-react";

export const MasterTeamPickerDialog = ({
  open, onOpenChange, loading, list, selected, onToggle, busy, onAdd,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-xl rounded-sm"
      data-testid="agency-rep-picker-dialog">
      <DialogHeader>
        <DialogTitle className="font-display font-bold text-2xl tracking-tight">
          Add from master team
        </DialogTitle>
        <DialogDescription className="text-sm text-zinc-500">
          Pick people from the master Iceberg Digital directory.
          They&apos;ll be linked to this client and stay in sync when you
          update their master entry. Already-added members are greyed
          out.
        </DialogDescription>
      </DialogHeader>

      {loading ? (
        <div className="py-10 text-center text-sm text-zinc-500"
          data-testid="agency-rep-picker-loading">Loading master team…</div>
      ) : list.length === 0 ? (
        <div className="py-10 text-center text-sm text-zinc-500"
          data-testid="agency-rep-picker-empty">
          The master team is empty — add people on the <strong>Team</strong> page first.
        </div>
      ) : (
        <div className="max-h-[420px] overflow-y-auto -mx-2 px-2 space-y-1"
          data-testid="agency-rep-picker-list">
          {list.map((m) => {
            const linked = m._already_linked;
            const checked = selected.has(m.team_member_id);
            return (
              <label key={m.team_member_id}
                className={`flex items-center gap-3 rounded-sm border p-3 transition-colors ${
                  linked
                    ? "border-zinc-200 bg-zinc-50 opacity-60 cursor-not-allowed"
                    : checked
                      ? "border-zinc-900 bg-zinc-50 cursor-pointer"
                      : "border-zinc-200 hover:bg-zinc-50/60 cursor-pointer"
                }`}
                data-testid={`agency-rep-picker-row-${m.team_member_id}`}>
                <input type="checkbox"
                  checked={checked}
                  disabled={linked}
                  onChange={() => !linked && onToggle(m.team_member_id)}
                  data-testid={`agency-rep-picker-checkbox-${m.team_member_id}`} />
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold text-sm truncate flex items-center gap-2">
                    {m.first_name} {m.last_name}
                    {linked && (
                      <span className="text-[9px] uppercase tracking-[0.15em] text-zinc-500 bg-zinc-100 px-1.5 py-0.5 rounded-sm font-mono"
                        data-testid={`agency-rep-picker-linked-${m.team_member_id}`}>
                        already added
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-zinc-500 truncate flex items-center gap-3 flex-wrap mt-0.5">
                    {m.title && (
                      <span className="inline-flex items-center gap-1">
                        <Briefcase className="h-3 w-3" /> {m.title}
                      </span>
                    )}
                    {m.email && (
                      <span className="inline-flex items-center gap-1">
                        <Mail className="h-3 w-3" /> {m.email}
                      </span>
                    )}
                    {m.phone && (
                      <span className="inline-flex items-center gap-1">
                        <Phone className="h-3 w-3" /> {m.phone}
                      </span>
                    )}
                  </div>
                </div>
              </label>
            );
          })}
        </div>
      )}

      <DialogFooter>
        <Button type="button" variant="ghost" className="rounded-sm"
          onClick={() => onOpenChange(false)}>Cancel</Button>
        <Button type="button"
          disabled={busy || selected.size === 0}
          className="rounded-sm bg-zinc-900 hover:bg-black text-white"
          onClick={onAdd}
          data-testid="agency-rep-picker-add-selected">
          {busy
            ? "Adding…"
            : `Add ${selected.size || ""} selected`.trim()}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
